/**
 * The vend visit as the API describes it (contracts/openapi.yaml, VendSession)
 * and what each state means to the customer.
 *
 * The page never decides a state itself: it renders the server's snapshot and
 * offers exactly the server's `allowed_actions` (ADR-0024, ADR-0025).
 */

/**
 * Methods this app can name. The list a customer sees comes from the store
 * (`GET /vend-sessions/{id}/channels`), after the machine reported the
 * selection, never from here.
 */
export const CHANNELS = ["paypay", "rakuten", "aupay", "docomopay", "wechat", "alipay", "aeonpay", "payjp"] as const;
export type Channel = string;

export interface ChannelOption {
  channel: Channel;
  /** False when the store offers it but this app cannot complete it yet (the card). */
  available: boolean;
  reason: string | null;
}

export type VendState =
  | "opened"
  | "selected"
  | "payment_pending"
  | "paid"
  | "driving"
  | "completed"
  | "failed"
  | "unknown"
  | "refund_due"
  | "refunded"
  | "canceled"
  | "expired";

export type VendAction = "pay" | "cancel" | "open_payment" | "request_refund" | "redrive";

export interface VendSession {
  vend_session_id: string;
  state: VendState;
  machine_id: string;
  /** Null until the customer chooses how to pay, after the selection. */
  channel: Channel | null;
  grid_no?: number | null;
  amount_yen?: number | null;
  product_name?: string | null;
  /** The machine's countdown: selection window while opened, occupancy while selected. */
  seconds_left?: number | null;
  outcome_reason?: string | null;
  payment?: { redirect_url?: string | null; deeplink?: string | null } | null;
  allowed_actions: VendAction[];
  unfulfilled: boolean;
  observed_at: string;
}

/** States after which nothing more will happen to the visit. */
export const TERMINAL: ReadonlySet<VendState> = new Set([
  "completed",
  "failed",
  "unknown",
  "refund_due",
  "refunded",
  "canceled",
  "expired",
]);

/**
 * States in which the customer still has something to do or wait for, so the
 * home screen offers to resume. `failed` and `refund_due` stay resumable while
 * a refund can still be requested.
 */
export function isResumable(v: VendSession): boolean {
  return !TERMINAL.has(v.state) || v.allowed_actions.includes("request_refund");
}

/** Every event kind the server writes; EventSource only delivers named events. */
export const EVENT_KINDS = [
  "opened",
  "machine_unavailable",
  "product_selected",
  "selection_changed",
  "payment_created",
  "paid",
  "drive_requested",
  "drive_sent",
  "dispensed",
  "dispense_failed",
  "dispense_unknown",
  "dispense_report_missing",
  "drive_unanswered",
  "drive_timed_out",
  "payment_unresolved",
  "refunded",
  "canceled",
  "expired",
  "resync",
] as const;

export type Tone = "waiting" | "action" | "progress" | "success" | "problem" | "neutral";

/**
 * How a state is shown: its tone and the step in progress on
 * Scan(0) > Product(1) > Pay(2) > Collect(3); 4 means every step is done.
 * A problem is always at Collect: the payment went through, the vend did not.
 */
export function presentation(state: VendState): { tone: Tone; step: 0 | 1 | 2 | 3 | 4 } {
  switch (state) {
    case "opened":
      return { tone: "waiting", step: 1 };
    case "selected":
      return { tone: "action", step: 2 };
    case "payment_pending":
      return { tone: "waiting", step: 2 };
    case "paid":
    case "driving":
      return { tone: "progress", step: 3 };
    case "completed":
      return { tone: "success", step: 4 };
    case "failed":
    case "refund_due":
    case "unknown":
      return { tone: "problem", step: 3 };
    case "refunded":
    case "canceled":
    case "expired":
      return { tone: "neutral", step: 0 };
  }
}

/**
 * Catalogue key for the line that explains how a visit ended, when its reason
 * has one (Android's wording), else null and the state's own hint is shown.
 */
export function outcomeKey(v: VendSession): string | null {
  const reason = v.outcome_reason;
  if (!reason) return null;
  const known = new Set([
    "selection_timeout", "choice_timeout", "auto_refunded", "upstream_refunded",
    "machine_busy", "machine_fault", "machine_canceled", "payment_timeout",
  ]);
  return known.has(reason) ? `vend.outcome.${reason}` : null;
}

export function formatYen(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: "JPY" }).format(amount);
}
